// src/components/dashboards/ReportGeneration.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import { receptionAPI, labAPI } from '../../services/api';
import { notify, confirmDialog } from '../../utils/notify';

// ─── Style injection ──────────────────────────────────────────────────────────
const injectStyles = () => {
  if (document.getElementById('rg-styles')) return;
  const style = document.createElement('style');
  style.id = 'rg-styles';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap');
    .rg-root *, .rg-root *::before, .rg-root *::after { box-sizing: border-box; font-family: 'DM Sans', sans-serif; }
    .rg-serif { font-family: 'Libre Baskerville', Georgia, serif !important; }
    .rg-input { width:100%; padding:9px 13px; border:1.5px solid #dde3ec; border-radius:8px; font-size:13px; outline:none; color:#1a2537; background:#fff; transition:border-color .15s; }
    .rg-input:focus { border-color:#1d6ca8; box-shadow:0 0 0 3px rgba(29,108,168,.1); }
    .rg-btn { padding:9px 18px; border:none; border-radius:8px; font-size:13px; font-weight:700; cursor:pointer; display:inline-flex; align-items:center; gap:7px; transition:all .15s; white-space:nowrap; }
    .rg-btn:disabled { opacity:.5; cursor:not-allowed !important; }
    .rg-btn-primary { background:linear-gradient(135deg,#1d6ca8,#14547e); color:#fff; box-shadow:0 2px 8px rgba(29,108,168,.3); }
    .rg-btn-primary:hover:not(:disabled) { filter:brightness(1.1); transform:translateY(-1px); }
    .rg-btn-outline { background:#fff; border:1.5px solid #dde3ec; color:#3d5166; }
    .rg-btn-outline:hover:not(:disabled) { background:#f4f7fb; }
    .rg-btn-teal { background:linear-gradient(135deg,#0d7377,#0a5c60); color:#fff; box-shadow:0 2px 8px rgba(13,115,119,.3); }
    .rg-btn-teal:hover:not(:disabled) { filter:brightness(1.1); transform:translateY(-1px); }
    .rg-btn-green { background:linear-gradient(135deg,#1a7a4a,#135e38); color:#fff; box-shadow:0 2px 8px rgba(26,122,74,.3); }
    .rg-btn-green:hover:not(:disabled) { filter:brightness(1.1); transform:translateY(-1px); }
    .rg-btn-amber { background:linear-gradient(135deg,#b45309,#92400e); color:#fff; box-shadow:0 2px 8px rgba(180,83,9,.3); }
    .rg-btn-amber:hover:not(:disabled) { filter:brightness(1.1); transform:translateY(-1px); }
    .rg-btn-violet { background:linear-gradient(135deg,#7c3aed,#5b21b6); color:#fff; box-shadow:0 2px 8px rgba(124,58,237,.3); }
    .rg-btn-violet:hover:not(:disabled) { filter:brightness(1.1); transform:translateY(-1px); }
    .rg-tr:hover td { background:#f0f6ff !important; cursor:pointer; }
    .rg-modal-overlay { position:fixed; inset:0; background:rgba(10,18,35,.65); display:flex; align-items:flex-start; justify-content:center; z-index:1000; backdrop-filter:blur(5px); animation:rgFade .18s ease; padding:20px 16px; overflow-y:auto; }
    .rg-modal { background:#f3f6fb; border-radius:18px; width:100%; max-width:960px; margin:auto; box-shadow:0 40px 120px rgba(10,18,35,.35); animation:rgSlide .22s ease; }
    .rg-section-card { background:#fff; border:1px solid #e4e9f0; border-radius:12px; padding:20px 24px; margin-bottom:14px; }
    .rg-label { font-size:10px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:#7e93a6; margin-bottom:4px; }
    .rg-value { font-size:13.5px; font-weight:500; color:#1a2537; line-height:1.5; }
    .rg-result-row:hover { background:#f4f9ff !important; }
    .rg-tab { padding:8px 18px; border-radius:8px; font-size:13px; font-weight:600; cursor:pointer; border:none; transition:all .15s; }
    .rg-tab-active { background:#1d6ca8; color:#fff; box-shadow:0 2px 8px rgba(29,108,168,.3); }
    .rg-tab-inactive { background:transparent; color:#7e93a6; }
    .rg-tab-inactive:hover { background:#e8f3fb; color:#1d6ca8; }
    @keyframes rgFade  { from{opacity:0} to{opacity:1} }
    @keyframes rgSlide { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
    @keyframes rgSpin  { to{transform:rotate(360deg)} }
    .rg-spinner { width:36px;height:36px;border:4px solid #e4e9f0;border-top-color:#1d6ca8;border-radius:50%;animation:rgSpin .8s linear infinite;margin:0 auto 16px; }
    ::-webkit-scrollbar{width:6px;height:6px} ::-webkit-scrollbar-thumb{background:#c5cdd8;border-radius:99px} ::-webkit-scrollbar-track{background:transparent}
    @media print {
      .rg-no-print { display: none !important; }
      .rg-print-only { display: block !important; }
    }
    .rg-print-only { display: none; }
  `;
  document.head.appendChild(style);
};

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  pageBg:'#edf1f7', navy:'#1a2537', navyMid:'#2c3e57', navyLight:'#3d5166',
  blue:'#1d6ca8', blueLight:'#e8f3fb', blueMid:'#cce4f6',
  teal:'#0d7377', tealLight:'#e0f5f5',
  green:'#1a7a4a', greenLight:'#e6f5ed', greenMid:'#b7e4c7',
  amber:'#b45309', amberLight:'#fef3c7', amberMid:'#fde68a',
  red:'#c0392b', redLight:'#fef2f0', redMid:'#fcc9c3',
  violet:'#7c3aed', violetLight:'#f5f3ff', violetMid:'#ddd6fe',
  slate:'#7e93a6', slate100:'#f4f7fb', slate200:'#e4e9f0',
  slate300:'#c5cdd8', slate600:'#4a5e70', slate700:'#2c3e57',
  textPrimary:'#1a2537', textSecondary:'#3d5166', textMuted:'#7e93a6',
  mono:"'JetBrains Mono','Courier New',monospace",
};

// ─── Formatters ───────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }); }
  catch { return String(d); }
};
const fmtTime = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' }); }
  catch { return ''; }
};
const fmtDateTime = (d) => d ? `${fmtDate(d)}, ${fmtTime(d)}` : '—';
const calcAge = (dob) => {
  if (!dob) return null;
  const yrs = Math.floor((Date.now() - new Date(dob)) / (1000 * 60 * 60 * 24 * 365.25));
  return isNaN(yrs) ? null : `${yrs} yrs`;
};
const sv = (v) => (v !== null && v !== undefined && String(v).trim()) ? String(v).trim() : null;

// ─── normalizeRecord ──────────────────────────────────────────────────────────
// Handles both full OPDRecord objects (from API) and compact token objects.
// Correctly maps the serializer field names: bp_sys/bp_dia, weight_kg, height_cm, etc.
const normalizeRecord = (raw = {}, tokenInfo = {}) => {
  if (!raw || Object.keys(raw).length === 0) {
    const nameParts = (tokenInfo.patient_name || '').split(' ');
    return {
      patient: {
        first_name: nameParts[0] || '', last_name: nameParts.slice(1).join(' ') || '',
        patient_id: tokenInfo.patient_id_num || null,
        gender: null, date_of_birth: null, phone: null, blood_group: null,
        emergency_contact: null, address: null, allergies: null, age: null,
      },
      token_info: tokenInfo,
      token_number: tokenInfo.token_number,
      visit_date: tokenInfo.created_at,
      department: tokenInfo.department_name,
      doctor_name: tokenInfo.doctor_name,
      vitals: {}, diagnoses: [], prescriptions: [],
    };
  }

  // ── Vitals: handle both raw API format (bp_sys/bp_dia) and pre-normalized ──
  const vitRaw = (raw.vitals && typeof raw.vitals === 'object') ? raw.vitals : {};
  const bpStr = vitRaw.bp_sys && vitRaw.bp_dia
    ? `${vitRaw.bp_sys}/${vitRaw.bp_dia} mmHg`
    : sv(vitRaw.blood_pressure || vitRaw.bp);
  const tempStr = vitRaw.temp != null
    ? `${vitRaw.temp}°${vitRaw.temp_unit || 'F'}`
    : sv(vitRaw.temperature);

  const vitals = {
    blood_pressure:   bpStr,
    pulse:            sv(vitRaw.pulse       || vitRaw.heart_rate),
    temperature:      tempStr,
    spo2:             sv(vitRaw.spo2        || vitRaw.oxygen_saturation),
    weight:           sv(vitRaw.weight_kg   || vitRaw.weight),
    height:           sv(vitRaw.height_cm   || vitRaw.height),
    bmi:              sv(vitRaw.bmi         || raw.bmi),
    respiratory_rate: sv(vitRaw.rr          || vitRaw.respiratory_rate),
  };

  // ── Symptoms ──────────────────────────────────────────────────────────────
  const sym = (raw.symptoms && typeof raw.symptoms === 'object') ? raw.symptoms : {};

  // ── Examination ───────────────────────────────────────────────────────────
  const exam = (raw.examination && typeof raw.examination === 'object') ? raw.examination : {};

  // ── Advice ────────────────────────────────────────────────────────────────
  const adv = (raw.advice_detail && typeof raw.advice_detail === 'object') ? raw.advice_detail : {};

  // ── Diagnoses ─────────────────────────────────────────────────────────────
  let diagnoses = raw.diagnosis_detail || raw.diagnoses || raw.diagnosis || [];
  if (typeof diagnoses === 'string' && diagnoses.trim()) diagnoses = [{ name: diagnoses }];
  if (!Array.isArray(diagnoses)) diagnoses = [];
  diagnoses = diagnoses.map(d => (typeof d === 'string' ? { name: d } : d));

  // ── Prescriptions ─────────────────────────────────────────────────────────
  let prescriptions = raw.prescription || raw.prescriptions || raw.medications || [];
  if (!Array.isArray(prescriptions)) prescriptions = [];

  // ── Investigations ────────────────────────────────────────────────────────
  let investigationsDetail = raw.investigations_detail || raw.investigations || [];
  if (!Array.isArray(investigationsDetail)) investigationsDetail = [];

  // ── Patient object ────────────────────────────────────────────────────────
  const pObj = (raw.patient && typeof raw.patient === 'object') ? raw.patient : {};
  const tokenNameParts = (tokenInfo.patient_name || '').split(' ');
  const snapshotParts = (raw.patient_name_snapshot || '').split(' ');

  const patient = {
    first_name:        pObj.first_name        || tokenNameParts[0]                  || snapshotParts[0]                  || '',
    last_name:         pObj.last_name         || tokenNameParts.slice(1).join(' ')  || snapshotParts.slice(1).join(' ')  || '',
    patient_id:        pObj.patient_id        || tokenInfo.patient_id_num           || pObj.id                           || null,
    date_of_birth:     pObj.date_of_birth     || pObj.dob                           || null,
    age:               pObj.age               || raw.patient_age                    || null,
    gender:            pObj.gender            || raw.patient_gender                 || null,
    phone:             pObj.phone             || pObj.phone_number                  || raw.patient_phone                 || null,
    blood_group:       pObj.blood_group       || null,
    emergency_contact: pObj.emergency_contact_name || pObj.emergency_contact || raw.next_of_kin || null,
    emergency_relation:pObj.emergency_contact_relation || null,
    address:           pObj.address           || raw.patient_address                || null,
    allergies:         pObj.allergies         || raw.patient_allergies              || null,
    current_medications: pObj.current_medications || null,
    previous_conditions: pObj.previous_conditions || null,
    aadhar:            pObj.aadhar_number     || pObj.aadhar                        || raw.patient_aadhar               || null,
    abha:              pObj.abha_number       || pObj.abha                          || raw.patient_abha                 || null,
    email:             pObj.email             || null,
  };

  // ── Doctor & Department ───────────────────────────────────────────────────
  let doctorName = raw.doctor_name || raw.assigned_doctor_name;
  if (!doctorName && raw.assigned_doctor && typeof raw.assigned_doctor === 'object')
    doctorName = `Dr. ${raw.assigned_doctor.first_name || ''} ${raw.assigned_doctor.last_name || ''}`.trim();
  if (!doctorName && raw.doctor && typeof raw.doctor === 'object')
    doctorName = `Dr. ${raw.doctor.first_name || ''} ${raw.doctor.last_name || ''}`.trim();
  if (!doctorName && typeof raw.doctor === 'string') doctorName = raw.doctor;
  if (!doctorName) doctorName = tokenInfo.doctor_name;

  let deptName = raw.department_name;
  if (!deptName && raw.department && typeof raw.department === 'object') deptName = raw.department.name;
  if (!deptName && typeof raw.department === 'string') deptName = raw.department;
  if (!deptName) deptName = tokenInfo.department_name;

  const tokenNumber = raw.token_number || tokenInfo.token_number
    || (raw.token && typeof raw.token === 'object' ? raw.token.token_number : null);

  return {
    ...raw,
    patient,
    token_info: tokenInfo,
    doctor_name: doctorName,
    department: deptName,
    token_number: tokenNumber,
    visit_date: raw.visit_date || raw.created_at,
    vitals,
    // Symptoms / History
    chief_complaint:            sv(sym.chief_complaint            || raw.chief_complaint),
    history_of_present_illness: sv(sym.history_of_present_illness || sym.history || sym.hpi || raw.history_of_present_illness || raw.history),
    associated_symptoms:        sym.associated_symptoms           || raw.associated_symptoms,
    duration_of_illness:        sv(sym.duration                   || raw.duration_of_illness),
    onset:                      sv(sym.onset                      || raw.onset),
    aggravating_factors:        sv(sym.aggravating_factors        || raw.aggravating_factors),
    relieving_factors:          sv(sym.relieving_factors          || raw.relieving_factors),
    past_medical_history:       sv(sym.past_medical_history       || raw.past_medical_history),
    surgical_history:           sv(sym.surgical_history           || raw.surgical_history),
    family_history:             sv(sym.family_history             || raw.family_history),
    drug_history:               sv(sym.drug_history               || raw.drug_history),
    social_history:             sv(sym.social_history             || raw.social_history),
    // Examination
    general_examination:        sv(exam.general_examination       || exam.general    || raw.general_examination),
    systemic_examination:       sv(exam.systemic_examination      || exam.systemic   || raw.systemic_examination || raw.examination_findings),
    cardiovascular:             sv(exam.cardiovascular            || exam.cvs        || raw.cardiovascular),
    respiratory:                sv(exam.respiratory               || exam.rs         || raw.respiratory),
    gastrointestinal:           sv(exam.gastrointestinal          || exam.git        || raw.gastrointestinal),
    central_nervous_system:     sv(exam.central_nervous_system    || exam.cns        || raw.central_nervous_system || raw.cns),
    musculoskeletal:            sv(exam.musculoskeletal           || exam.mss        || raw.musculoskeletal),
    local_examination:          sv(exam.local_examination         || exam.local      || raw.local_examination),
    // Diagnosis & Treatment
    diagnoses,
    differential_diagnosis:     sv(raw.differential_diagnosis),
    prescriptions,
    investigations_detail: investigationsDetail,
    investigations_text:        sv(typeof raw.investigations === 'string' ? raw.investigations : null),
    treatment_plan:             sv(adv.treatment_plan             || raw.treatment_plan),
    // Advice
    advice:                     sv(adv.advice || (typeof raw.advice === 'string' ? raw.advice : null)),
    diet_advice:                sv(adv.diet_advice                || raw.diet_advice),
    activity_restriction:       sv(adv.activity_restriction       || raw.activity_restriction),
    follow_up_instructions:     sv(adv.follow_up_instructions     || adv.follow_up  || raw.follow_up_instructions),
    follow_up_date:             sv(adv.follow_up_date             || raw.follow_up_date),
    // Notes & metadata
    doctor_notes:               sv(raw.doctor_notes               || raw.notes      || raw.clinical_notes),
    referral:                   raw.referral || null,
    consultation_started_at:    raw.consultation_started_at       || null,
    consultation_completed_at:  raw.consultation_completed_at     || raw.updated_at || null,
    contributors:               raw.contributors                  || {},
  };
};

// ─── Shared UI primitives ─────────────────────────────────────────────────────
const InfoPair = ({ label, value, mono, wide }) => (
  <div style={{ minWidth: 0, gridColumn: wide ? 'span 2' : undefined }}>
    <div className="rg-label">{label}</div>
    <div className="rg-value" style={{ wordBreak: 'break-word', fontFamily: mono ? C.mono : undefined }}>
      {value || '—'}
    </div>
  </div>
);

const SectionHeader = ({ icon, title, color = C.navy, count }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: `2px solid ${C.slate200}` }}>
    <div style={{ width: 34, height: 34, borderRadius: 9, background: color + '1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>{icon}</div>
    <h3 className="rg-serif" style={{ margin: 0, fontSize: 14.5, fontWeight: 700, color, flexGrow: 1 }}>{title}</h3>
    {count !== undefined && (
      <span style={{ background: color + '1a', color, padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 800 }}>{count}</span>
    )}
  </div>
);

const ProseBlock = ({ value, color = C.blue }) => (
  <div style={{ fontSize: 13.5, color: C.textPrimary, lineHeight: 1.8, background: C.slate100, borderLeft: `3px solid ${color}`, padding: '10px 16px', borderRadius: '0 8px 8px 0', marginTop: 4, whiteSpace: 'pre-wrap' }}>
    {value}
  </div>
);

const FlagPill = ({ flag }) => {
  const map = {
    NORMAL:   { label: 'Normal',      bg: C.greenMid,  color: C.green },
    HIGH:     { label: '↑ High',      bg: C.amberMid,  color: C.amber },
    LOW:      { label: '↓ Low',       bg: C.blueMid,   color: C.blue  },
    CRITICAL: { label: '‼ Critical',  bg: C.redMid,    color: C.red   },
  };
  const m = map[flag] || map.NORMAL;
  return (
    <span style={{ background: m.bg, color: m.color, padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
};

const EmptyState = ({ message }) => (
  <div style={{ color: C.textMuted, fontStyle: 'italic', fontSize: 13, padding: '4px 0' }}>{message}</div>
);

const LoadingSpinner = ({ message = 'Loading…' }) => (
  <div style={{ textAlign: 'center', padding: '56px 0', color: C.textMuted }}>
    <div className="rg-spinner" />
    <div style={{ fontWeight: 600, fontSize: 15 }}>{message}</div>
  </div>
);

// ─── downloadHTML ─────────────────────────────────────────────────────────────
const downloadHTML = (html, filename) => {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 3000);
};

// ─── printInNewTab ────────────────────────────────────────────────────────────
const printInNewTab = (html, title) => {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) { notify.info('Please allow popups for printing.'); return; }
  win.document.write(html);
  win.document.close();
  win.onload = () => {
    setTimeout(() => {
      win.focus();
      win.print();
    }, 400);
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// HTML REPORT GENERATORS
// These produce self-contained HTML documents for download / print.
// ─────────────────────────────────────────────────────────────────────────────

// ─── generateLabReportHTML ────────────────────────────────────────────────────
const generateLabReportHTML = (record, labOrders) => {
  const p = record.patient || {};
  const patName = `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—';
  const now = new Date().toISOString();

  const ordersHtml = labOrders.map(order => {
    const sampleBarcode = order.sample?.barcode || order.barcode_id || `LAB-${order.id}`;
    const collectedAt   = order.sample?.collected_at || order.order_date;
    const receivedAt    = order.sample?.collected_at || order.updated_at || collectedAt;
    const reportedAt    = order.status === 'COMPLETED' ? (order.updated_at || now) : now;
    const category      = order.items?.[0]?.test?.category_name || order.items?.[0]?.test?.category?.name || '';
    const refBy         = record.department
      ? `${record.department}${category ? ' (' + category + ')' : ''}`
      : (category || record.doctor_name || '—');

    const itemsHtml = (order.items || []).map(item => {
      const params   = item.test?.parameters || item.parameters || [];
      const testName = item.test_name || item.test?.name || 'Unknown Test';
      const testCode = item.test_code || item.test?.code || '';

      const paramRows = params.map(param => {
        const r = (order.results || []).find(res =>
          res.parameter === param.id || res.parameter_id === param.id
        );
        const val = r?.value_numeric != null ? String(r.value_numeric) : (r?.value_text || '');
        const ref = param.normal_range_text
          || (param.normal_min != null && param.normal_max != null ? `${param.normal_min} – ${param.normal_max}` : '');
        const flag = r?.flag || (val ? 'NORMAL' : '');
        const isAbn = flag === 'HIGH' || flag === 'LOW' || flag === 'CRITICAL';
        const sym  = flag === 'HIGH' ? '↑' : flag === 'LOW' ? '↓' : flag === 'CRITICAL' ? '‼' : '';
        const rowBg = flag === 'CRITICAL' ? '#fff0f0' : isAbn ? '#fffbeb' : '';
        const valColor = flag === 'CRITICAL' ? '#c0392b' : isAbn ? '#b45309' : '#1a2537';

        return `<tr style="border-bottom:1px solid #e8ecf0;background:${rowBg}">
          <td style="padding:9px 14px;color:#555;font-size:12px">${param.specimen || 'Serum'}</td>
          <td style="padding:9px 14px;font-size:13px;color:#1a2537">${param.name}${param.unit ? `<span style="color:#999;font-size:11px;margin-left:4px">(${param.unit})</span>` : ''}</td>
          <td style="padding:9px 14px;font-size:14px;font-weight:${isAbn ? '800' : '500'};color:${valColor}">${val || '—'}</td>
          <td style="padding:9px 14px;font-size:13px;font-weight:800;color:${valColor}">${sym}</td>
          <td style="padding:9px 14px;font-size:12px;color:#555">${param.unit || '—'}</td>
          <td style="padding:9px 14px;font-size:12px;color:#444;white-space:pre-line">${ref || '—'}</td>
          <td style="padding:9px 14px;font-size:12px;color:#555">${r?.notes || ''}</td>
        </tr>`;
      }).join('');

      const noParams = params.length === 0
        ? `<tr><td colspan="7" style="padding:14px;color:#999;font-style:italic;font-size:12px">No parameters recorded for this test</td></tr>` : '';

      return `
        <tr style="background:#e8edf2">
          <td colspan="7" style="padding:8px 14px">
            <span style="font-weight:800;font-size:13px;color:#1a2537;text-transform:uppercase;letter-spacing:.04em">${testName}</span>
            ${testCode ? `<span style="font-size:11px;color:#7e93a6;margin-left:8px">${testCode}</span>` : ''}
            <span style="font-size:11px;color:#7e93a6;margin-left:12px">Status: ${item.status_display || item.status || '—'}</span>
          </td>
        </tr>
        ${paramRows}${noParams}`;
    }).join('');

    return `
<div style="page-break-inside:avoid;margin-bottom:44px">
  <div style="border:1.5px solid #b0bec5;border-radius:8px 8px 0 0;overflow:hidden">
    <div style="background:#1a2537;color:#fff;padding:12px 20px;display:flex;justify-content:space-between;align-items:center">
      <div>
        <span style="font-family:Georgia,serif;font-size:18px;font-weight:700;letter-spacing:.5px">Laboratory Investigation Report</span>
        <span style="font-size:11px;opacity:.7;margin-left:14px">${category ? category.toUpperCase() : 'CLINICAL PATHOLOGY'}</span>
      </div>
      <div style="font-size:12px;opacity:.8">Report Date: ${fmtDateTime(reportedAt)}</div>
    </div>

    <div style="background:#f5f7fa;padding:16px 20px;display:grid;grid-template-columns:1fr 1fr;gap:8px 48px;border-bottom:1px solid #d0d8e0">
      ${[
        ['PATIENT ID',     p.patient_id || '—'],
        ['SAMPLE BARCODE', sampleBarcode],
        ['PATIENT NAME',   patName],
        ['COLLECTED DATE', fmtDateTime(collectedAt)],
        ['AGE / GENDER',   `${p.age ? p.age + ' Y' : (calcAge(p.date_of_birth) || '—')} / ${p.gender || '—'}`],
        ['RECEIVED DATE',  fmtDateTime(receivedAt)],
        ['REF. BY DEPT',   refBy],
        ['REPORTED DATE',  fmtDateTime(reportedAt)],
        ['OPD TOKEN',      record.token_number || '—'],
        ['ORDER STATUS',   `${order.status || '—'} · ${order.urgency || 'ROUTINE'}`],
      ].map(([label, val]) => `
        <div style="display:flex;gap:8px">
          <span style="font-weight:700;color:#444;font-size:11px;min-width:130px;flex-shrink:0">${label}</span>
          <span style="font-size:12px;color:#1a2537;font-weight:${label === 'PATIENT NAME' ? '700' : '400'}">: ${val}</span>
        </div>`).join('')}
    </div>
  </div>

  <table style="width:100%;border-collapse:collapse;border:1.5px solid #b0bec5;border-top:none;font-size:13px">
    <thead>
      <tr style="background:#dce3ea;border-bottom:2px solid #9aaab8">
        ${['Specimen','Test Name','Result','Flag','Units','Reference Range','Notes'].map(h =>
          `<th style="padding:9px 14px;text-align:left;font-size:11px;font-weight:700;color:#37474f;letter-spacing:.04em;text-transform:uppercase">${h}</th>`
        ).join('')}
      </tr>
    </thead>
    <tbody>${itemsHtml || '<tr><td colspan="7" style="padding:20px;text-align:center;color:#999">No test items in this order</td></tr>'}</tbody>
  </table>

  ${order.special_instructions
    ? `<div style="padding:10px 20px;background:#fffbeb;border:1.5px solid #b0bec5;border-top:none;font-size:12px;color:#78350f"><strong>Comments / Instructions:</strong> ${order.special_instructions}</div>`
    : ''}

  <div style="border:1.5px solid #b0bec5;border-top:none;padding:14px 20px;display:flex;justify-content:space-between;align-items:flex-end;background:#fafbfc">
    <div style="font-size:11px;color:#7e93a6">
      <div>Urgency: <strong style="color:#1a2537">${order.urgency || 'ROUTINE'}</strong></div>
      <div style="margin-top:4px">Order #${order.id} &nbsp;·&nbsp; Payment: <strong>${order.payment_confirmed ? 'Confirmed' : order.bill_generated ? 'Billed' : 'Pending'}</strong></div>
      <div style="margin-top:4px">Generated: ${fmtDateTime(now)}</div>
    </div>
    <div style="text-align:center">
      <div style="border-top:1px solid #1a2537;width:180px;padding-top:8px;margin-left:auto">
        <div style="font-size:12px;font-weight:700;color:#1a2537">${record.doctor_name || 'Lab Director'}</div>
        <div style="font-size:11px;color:#7e93a6">Authorized Signatory</div>
      </div>
    </div>
  </div>
</div>`;
  }).join('\n');

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/>
<title>Lab Report — ${patName}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#1a2537;background:#f0f4f8;font-size:13px;line-height:1.6}
  .page{max-width:960px;margin:0 auto;padding:32px 24px}
  @media print{body{background:#fff}.page{padding:14px 0}@page{margin:1.5cm}div{page-break-inside:avoid}}
</style>
</head><body><div class="page">
  <div style="text-align:center;margin-bottom:28px;padding-bottom:18px;border-bottom:2.5px solid #1a2537">
    <div style="font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#1d6ca8;margin-bottom:6px">Medical ERP System · Laboratory Division</div>
    <h1 style="font-family:Georgia,serif;font-size:24px;font-weight:700;color:#1a2537">Laboratory Investigation Report</h1>
    <div style="font-size:11px;color:#7e93a6;margin-top:5px">This report is generated electronically. Results should be interpreted in clinical context.</div>
  </div>
  ${labOrders.length === 0
    ? '<div style="text-align:center;padding:80px 0;color:#7e93a6;font-size:15px;font-style:italic">No lab orders found for this visit</div>'
    : ordersHtml}
</div></body></html>`;
};

// ─── generateSummaryHTML ──────────────────────────────────────────────────────
const generateSummaryHTML = (record, labOrders) => {
  const p    = record.patient || {};
  const vit  = record.vitals  || {};
  const meds = record.prescriptions || [];
  const diagnoses = record.diagnoses || [];

  const patName = `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—';

  const prose = (t) => t
    ? `<div style="font-size:13px;line-height:1.8;background:#f9fafb;border-left:3px solid #1d6ca8;padding:12px 16px;border-radius:0 8px 8px 0;white-space:pre-wrap">${t}</div>`
    : '';

  const gridRow = (pairs) =>
    `<div style="display:grid;grid-template-columns:repeat(4,1fr);gap:10px 20px;margin-bottom:12px">
      ${pairs.map(([l, v]) => v ? `<div><div style="font-size:10px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#7e93a6;margin-bottom:2px">${l}</div><div style="font-size:13px;font-weight:500;color:#1a2537">${v}</div></div>` : '').join('')}
    </div>`;

  const vitalItems = [
    ['Blood Pressure', vit.blood_pressure],
    ['Pulse',          vit.pulse        ? `${vit.pulse} bpm`      : null],
    ['Temperature',    vit.temperature],
    ['SpO₂',           vit.spo2         ? `${vit.spo2}%`          : null],
    ['Weight',         vit.weight       ? `${vit.weight} kg`      : null],
    ['Height',         vit.height       ? `${vit.height} cm`      : null],
    ['BMI',            vit.bmi],
    ['Resp Rate',      vit.respiratory_rate ? `${vit.respiratory_rate}/min` : null],
  ].filter(([, v]) => v);

  const diagHtml = diagnoses.length
    ? diagnoses.map((d, i) => `<div style="display:flex;gap:12px;padding:10px 0;border-bottom:1px solid #edf1f7">
        <span style="font-weight:800;color:#1d6ca8;min-width:22px">${i + 1}.</span>
        <div>
          <div style="font-weight:700;font-size:14px">${d.name || d.diagnosis_name || String(d)}</div>
          ${d.icd_code ? `<div style="font-size:11px;color:#7e93a6;font-family:monospace">ICD: ${d.icd_code}</div>` : ''}
          ${d.notes    ? `<div style="font-size:13px;color:#3d5166;margin-top:4px;line-height:1.5">${d.notes}</div>` : ''}
        </div>
      </div>`).join('')
    : '<div style="color:#7e93a6;font-style:italic">No diagnoses recorded</div>';

  const medHtml = meds.length
    ? `<table style="width:100%;border-collapse:collapse;font-size:12px">
        <thead><tr style="background:#edf1f7">${['#','Medication','Dose / Form','Route','Frequency','Duration','Instructions'].map(h =>
          `<th style="padding:7px 12px;text-align:left;color:#7e93a6;font-size:10px;text-transform:uppercase;letter-spacing:.06em">${h}</th>`
        ).join('')}</tr></thead>
        <tbody>${meds.map((m, i) => `
          <tr style="border-bottom:1px solid #f4f7fb">
            <td style="padding:8px 12px;color:#7e93a6">${i + 1}</td>
            <td style="padding:8px 12px;font-weight:700">${m.drug_name || m.name || '—'}</td>
            <td style="padding:8px 12px">${[m.dose || m.dosage, m.form].filter(Boolean).join(' ') || '—'}</td>
            <td style="padding:8px 12px">${m.route || '—'}</td>
            <td style="padding:8px 12px">${m.frequency || '—'}</td>
            <td style="padding:8px 12px">${[m.duration, m.duration_unit].filter(Boolean).join(' ') || '—'}</td>
            <td style="padding:8px 12px;font-size:11px">${m.instructions || m.special_instructions || '—'}</td>
          </tr>`).join('')}</tbody>
      </table>`
    : '<div style="color:#7e93a6;font-style:italic">No medications prescribed</div>';

  const labHtml = labOrders.map(order => {
    const items = (order.items || []).map(item => {
      const params   = item.test?.parameters || item.parameters || [];
      const rows = params.map(param => {
        const r = (order.results || []).find(res => res.parameter === param.id || res.parameter_id === param.id);
        const val = r?.value_numeric != null ? `${r.value_numeric} ${param.unit || ''}` : (r?.value_text || '—');
        const ref = param.normal_range_text || (param.normal_min != null ? `${param.normal_min}–${param.normal_max}` : '—');
        const fc  = !r?.flag || r.flag === 'NORMAL' ? '#1a7a4a' : r.flag === 'CRITICAL' ? '#c0392b' : '#b45309';
        return `<tr>
          <td style="padding:6px 10px">${param.name}${param.unit ? ` (${param.unit})` : ''}</td>
          <td style="padding:6px 10px;font-weight:700">${val}</td>
          <td style="padding:6px 10px;color:#7e93a6">${ref}</td>
          <td style="padding:6px 10px;font-weight:700;color:${fc}">${r?.flag || '—'}</td>
          <td style="padding:6px 10px;font-size:11px;color:#555">${r?.notes || ''}</td>
        </tr>`;
      }).join('');
      return `<div style="margin-bottom:10px">
        <div style="font-weight:800;font-size:13px;background:#f4f7fb;padding:6px 12px;border-left:3px solid #1d6ca8">${item.test_name || item.test?.name}</div>
        <table style="width:100%;border-collapse:collapse;font-size:12px">
          <thead><tr style="background:#edf1f7">
            ${['Parameter','Result','Reference Range','Flag','Notes'].map(h =>
              `<th style="padding:5px 10px;text-align:left;color:#7e93a6;font-size:10px">${h}</th>`
            ).join('')}
          </tr></thead>
          <tbody>${rows || '<tr><td colspan="5" style="padding:8px 10px;color:#999;font-style:italic">No parameters</td></tr>'}</tbody>
        </table></div>`;
    }).join('');
    return `<div style="margin-bottom:18px;border:1px solid #e4e9f0;border-radius:8px;overflow:hidden">
      <div style="background:#1a2537;color:#fff;padding:8px 16px;display:flex;justify-content:space-between">
        <span style="font-weight:700;font-size:13px">Order #${order.id} — ${order.urgency || 'ROUTINE'}</span>
        <span style="opacity:.7;font-size:11px">${fmtDateTime(order.order_date)} · ${order.status}</span>
      </div>
      ${order.sample ? `<div style="background:#f4f7fb;padding:8px 16px;font-size:11px;color:#3d5166">Sample Barcode: <strong>${order.sample.barcode}</strong> &nbsp;·&nbsp; Collected: ${fmtDateTime(order.sample.collected_at)}</div>` : ''}
      <div style="padding:12px 16px">${items}</div>
      ${order.special_instructions ? `<div style="padding:8px 16px;background:#fffbeb;border-top:1px solid #fde68a;font-size:12px;color:#78350f"><strong>Comments:</strong> ${order.special_instructions}</div>` : ''}
    </div>`;
  }).join('');

  const invHtml = (record.investigations_detail || []).length
    ? `<table style="width:100%;border-collapse:collapse;font-size:12px">
        <thead><tr style="background:#edf1f7">
          ${['#','Investigation','Notes'].map(h =>
            `<th style="padding:7px 12px;text-align:left;color:#7e93a6;font-size:10px">${h}</th>`
          ).join('')}
        </tr></thead>
        <tbody>${record.investigations_detail.map((inv, i) =>
          `<tr style="border-bottom:1px solid #f4f7fb">
            <td style="padding:7px 12px;color:#7e93a6">${i + 1}</td>
            <td style="padding:7px 12px;font-weight:700">${inv.name || '—'}</td>
            <td style="padding:7px 12px">${inv.notes || inv.instructions || ''}</td>
          </tr>`).join('')}</tbody>
      </table>`
    : (record.investigations_text ? `<div style="font-size:13px;color:#3d5166;background:#f9fafb;border-left:3px solid #1d6ca8;padding:10px 14px;border-radius:0 8px 8px 0">${record.investigations_text}</div>` : '<div style="color:#7e93a6;font-style:italic">No investigations ordered</div>');

  const section = (title, content) =>
    `<div style="margin-bottom:22px;page-break-inside:avoid">
      <div style="font-family:Georgia,serif;font-size:13px;font-weight:700;color:#1d6ca8;text-transform:uppercase;letter-spacing:.08em;border-bottom:2px solid #1d6ca8;padding-bottom:5px;margin-bottom:12px">${title}</div>
      ${content}
    </div>`;

  return `<!DOCTYPE html><html lang="en">
<head><meta charset="UTF-8"/>
<title>Patient Summary — ${patName}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Segoe UI',Arial,sans-serif;color:#1a2537;background:#fff;font-size:13px;line-height:1.65}
  .page{max-width:900px;margin:0 auto;padding:32px 40px}
  .grid-4{display:grid;grid-template-columns:repeat(4,1fr);gap:10px 20px;margin-bottom:10px}
  .label{font-size:10px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#7e93a6;margin-bottom:2px}
  .value{font-size:13px;font-weight:500;color:#1a2537}
  @media print{body{font-size:12px}.page{padding:12px 18px}div{page-break-inside:avoid}@page{margin:1.2cm}}
</style></head>
<body><div class="page">

  <!-- Header -->
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:18px;border-bottom:2.5px solid #1a2537">
    <div>
      <div style="font-size:11px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#1d6ca8;margin-bottom:6px">Medical ERP System</div>
      <h1 style="font-family:Georgia,serif;font-size:24px;font-weight:700;color:#1a2537;margin-bottom:4px">Comprehensive Patient Summary</h1>
      <div style="font-size:11px;color:#7e93a6">Generated: ${fmtDateTime(new Date().toISOString())}</div>
    </div>
    <div style="text-align:right">
      <div class="label">OPD Token</div>
      <div style="font-size:28px;font-weight:800;color:#1d6ca8;font-family:monospace;letter-spacing:2px">${record.token_number || '—'}</div>
      <div style="font-size:11px;color:#7e93a6">Record #${record.id || '—'}</div>
      <div style="display:inline-block;background:#b7e4c7;color:#1a7a4a;padding:3px 12px;border-radius:99px;font-size:11px;font-weight:800;margin-top:6px">✓ ${record.status || 'COMPLETED'}</div>
    </div>
  </div>

  <!-- Demographics -->
  ${section('Patient Demographics', `
    <div class="grid-4" style="margin-bottom:12px">
      <div><div class="label">Full Name</div><div class="value" style="font-size:16px;font-weight:700">${patName}</div></div>
      <div><div class="label">Patient ID</div><div class="value" style="font-family:monospace;font-weight:700">${p.patient_id || '—'}</div></div>
      <div><div class="label">Age</div><div class="value">${p.age ? p.age + ' yrs' : (calcAge(p.date_of_birth) || '—')}</div></div>
    </div>
    <div class="grid-4" style="margin-bottom:12px">
      <div><div class="label">Gender</div><div class="value">${p.gender || '—'}</div></div>
      <div><div class="label">Phone</div><div class="value">${p.phone || '—'}</div></div>
      <div><div class="label">Email</div><div class="value">${p.email || '—'}</div></div>
    </div>
    <div class="grid-4" style="margin-bottom:12px">
      <div><div class="label">Emergency Contact</div><div class="value">${p.emergency_contact || '—'}</div></div>
      <div><div class="label">Relation</div><div class="value">${p.emergency_relation || '—'}</div></div>
      <div><div class="label">Aadhar Number</div><div class="value" style="font-family:monospace">${p.aadhar || '—'}</div></div>
      <div><div class="label">ABHA Number</div><div class="value" style="font-family:monospace">${p.abha || '—'}</div></div>
    </div>
    ${p.address ? `<div style="margin-bottom:10px"><div class="label">Address</div><div class="value">${p.address}</div></div>` : ''}
    ${p.previous_conditions ? `<div style="margin-bottom:10px"><div class="label">Known Medical Conditions</div><div class="value">${p.previous_conditions}</div></div>` : ''}
    ${p.current_medications ? `<div style="margin-bottom:10px"><div class="label">Current Medications (Pre-Visit)</div><div class="value">${p.current_medications}</div></div>` : ''}
    ${p.allergies ? `<div style="background:#fef3c7;border-left:3px solid #f59e0b;padding:10px 14px;border-radius:0 8px 8px 0"><div class="label" style="color:#92400e">⚠ Known Allergies</div><div class="value" style="color:#92400e;font-weight:700;font-size:14px">${p.allergies}</div></div>` : ''}
  `)}

  <!-- Visit Info -->
  ${section('Visit Information', `
    <div class="grid-4" style="margin-bottom:12px">
      <div><div class="label">Visit Date</div><div class="value">${fmtDate(record.visit_date || record.created_at)}</div></div>
      <div><div class="label">Visit Time</div><div class="value">${fmtTime(record.visit_date || record.created_at)}</div></div>
      <div><div class="label">OPD Token</div><div class="value" style="font-family:monospace;font-weight:700">${record.token_number || '—'}</div></div>
      <div><div class="label">Department</div><div class="value">${record.department || '—'}</div></div>
    </div>
    <div class="grid-4">
      <div><div class="label">Consulting Doctor</div><div class="value">${record.doctor_name || '—'}</div></div>
      <div><div class="label">Consultation Started</div><div class="value">${fmtDateTime(record.consultation_started_at)}</div></div>
      <div><div class="label">Consultation Completed</div><div class="value">${fmtDateTime(record.consultation_completed_at)}</div></div>
      <div><div class="label">Follow-Up Date</div><div class="value">${fmtDate(record.follow_up_date)}</div></div>
    </div>
  `)}

  <!-- Vitals -->
  ${section('Vital Signs', vitalItems.length
    ? `<div style="display:flex;flex-wrap:wrap;gap:12px">${vitalItems.map(([l, v]) =>
        `<div style="background:#f4f7fb;border-radius:8px;padding:10px 16px;text-align:center;min-width:110px;border:1px solid #e4e9f0">
          <div style="font-size:18px;font-weight:800;color:#1d6ca8;margin-bottom:4px">${v}</div>
          <div style="font-size:10px;color:#7e93a6;font-weight:700;text-transform:uppercase;letter-spacing:.05em">${l}</div>
        </div>`).join('')}</div>`
    : '<div style="color:#7e93a6;font-style:italic">No vitals recorded</div>'
  )}

  <!-- History -->
  ${section('Chief Complaint & Clinical History', (() => {
    const fields = [
      ['Chief Complaint',            record.chief_complaint],
      ['History of Present Illness', record.history_of_present_illness],
      ['Duration of Illness',        record.duration_of_illness],
      ['Onset',                      record.onset],
      ['Aggravating Factors',        record.aggravating_factors],
      ['Relieving Factors',          record.relieving_factors],
      ['Associated Symptoms',        Array.isArray(record.associated_symptoms) ? record.associated_symptoms.join(', ') : record.associated_symptoms],
      ['Past Medical History',       record.past_medical_history],
      ['Surgical History',           record.surgical_history],
      ['Family History',             record.family_history],
      ['Drug History',               record.drug_history],
      ['Social History',             record.social_history],
    ].filter(([, v]) => v);
    return fields.length
      ? fields.map(([l, v]) => `<div style="margin-bottom:12px"><div class="label">${l}</div>${prose(v)}</div>`).join('')
      : '<div style="color:#7e93a6;font-style:italic">No complaint or history recorded</div>';
  })())}

  <!-- Examination -->
  ${section('Physical Examination Findings', (() => {
    const fields = [
      ['General Examination',   record.general_examination],
      ['Systemic Examination',  record.systemic_examination],
      ['Cardiovascular System', record.cardiovascular],
      ['Respiratory System',    record.respiratory],
      ['Gastrointestinal',      record.gastrointestinal],
      ['Central Nervous System',record.central_nervous_system],
      ['Musculoskeletal',       record.musculoskeletal],
      ['Local Examination',     record.local_examination],
    ].filter(([, v]) => v);
    return fields.length
      ? fields.map(([l, v]) => `<div style="margin-bottom:12px"><div class="label">${l}</div>${prose(v)}</div>`).join('')
      : '<div style="color:#7e93a6;font-style:italic">No examination findings recorded</div>';
  })())}

  <!-- Diagnosis -->
  ${section('Diagnosis', `
    ${diagHtml}
    ${record.differential_diagnosis ? `<div style="margin-top:12px"><div class="label">Differential Diagnosis</div>${prose(record.differential_diagnosis)}</div>` : ''}
  `)}

  <!-- Prescriptions -->
  ${section('Prescriptions & Medications', `
    ${medHtml}
    ${record.treatment_plan ? `<div style="margin-top:14px"><div class="label">Treatment Plan</div>${prose(record.treatment_plan)}</div>` : ''}
  `)}

  <!-- Investigations Ordered -->
  ${section('Investigations Ordered', invHtml)}

  <!-- Lab Results -->
  ${section(`Laboratory Results (${labOrders.length} Order${labOrders.length !== 1 ? 's' : ''})`,
    labOrders.length
      ? `<div style="margin-bottom:10px;font-size:11px;color:#7e93a6">${labOrders.filter(o => o.status === 'COMPLETED').length} of ${labOrders.length} order(s) completed</div>${labHtml}`
      : '<div style="color:#7e93a6;font-style:italic">No laboratory investigations ordered for this visit</div>'
  )}

  <!-- Doctor Notes -->
  ${record.doctor_notes ? section('Doctor Notes & Clinical Remarks', prose(record.doctor_notes)) : ''}

  <!-- Advice -->
  ${[record.advice, record.diet_advice, record.activity_restriction, record.follow_up_instructions].some(Boolean)
    ? section('Advice & Follow-Up Instructions', [
        ['Advice',                record.advice],
        ['Diet Advice',           record.diet_advice],
        ['Activity Restrictions', record.activity_restriction],
        ['Follow-Up Instructions',record.follow_up_instructions],
        ['Follow-Up Date',        fmtDate(record.follow_up_date)],
      ].filter(([, v]) => v).map(([l, v]) =>
        `<div style="margin-bottom:12px"><div class="label">${l}</div>${l === 'Follow-Up Date' ? `<div class="value" style="font-weight:700;color:#1d6ca8">${v}</div>` : prose(v)}</div>`
      ).join(''))
    : ''}

  <!-- Footer -->
  <div style="margin-top:36px;padding-top:20px;border-top:2px solid #1a2537;display:flex;justify-content:space-between;align-items:flex-end">
    <div style="font-size:11px;color:#c5cdd8">
      <div>Printed: ${fmtDateTime(new Date().toISOString())}</div>
      <div style="margin-top:3px">Medical ERP System · Confidential Patient Record</div>
    </div>
    <div style="text-align:center">
      <div style="border-top:1.5px solid #1a2537;width:200px;padding-top:8px;margin-left:auto">
        <div style="font-size:13px;font-weight:700;color:#1a2537">${record.doctor_name || 'Treating Physician'}</div>
        <div style="font-size:11px;color:#7e93a6">Signature &amp; Stamp</div>
      </div>
    </div>
  </div>

</div></body></html>`;
};

// ─────────────────────────────────────────────────────────────────────────────
// MODAL COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

// ─── LabResultsGrid — used inside both modals ─────────────────────────────────
const LabResultsGrid = ({ labOrders }) => {
  if (!labOrders.length) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 0', color: C.textMuted }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>🧪</div>
        <div style={{ fontWeight: 600, fontSize: 15 }}>No lab orders found for this visit</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 14 }}>
        {labOrders.length} order(s) &nbsp;·&nbsp; {labOrders.filter(o => o.status === 'COMPLETED').length} completed
        &nbsp;·&nbsp; {labOrders.filter(o => o.payment_confirmed).length} paid
      </div>
      {labOrders.map(order => (
        <div key={order.id} style={{ border: `1.5px solid ${C.slate200}`, borderRadius: 12, overflow: 'hidden', marginBottom: 18 }}>
          {/* Order header */}
          <div style={{ background: C.navy, color: '#fff', padding: '10px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>Order #{order.id}</span>
              <span style={{
                background: order.urgency === 'STAT' ? C.red : order.urgency === 'URGENT' ? C.amber : '#4a5e70',
                padding: '2px 9px', borderRadius: 5, fontSize: 11, fontWeight: 700
              }}>{order.urgency || 'ROUTINE'}</span>
              <span style={{
                background: order.status === 'COMPLETED' ? C.green : order.status === 'IN_PROGRESS' ? C.teal : '#4a5e70',
                padding: '2px 9px', borderRadius: 5, fontSize: 11, fontWeight: 700
              }}>{order.status}</span>
            </div>
            <div style={{ fontSize: 11, opacity: .7, textAlign: 'right' }}>
              <div>{fmtDateTime(order.order_date)}</div>
              {order.payment_confirmed && <div style={{ color: C.greenMid }}>✓ Payment Confirmed</div>}
            </div>
          </div>

          {/* Sample info */}
          {order.sample && (
            <div style={{ background: C.greenLight, padding: '8px 18px', borderBottom: `1px solid ${C.greenMid}`, fontSize: 12, display: 'flex', gap: 24 }}>
              <span>🧫 Barcode: <strong style={{ fontFamily: C.mono }}>{order.sample.barcode}</strong></span>
              <span>Type: <strong>{order.sample.sample_type || '—'}</strong></span>
              <span>Collected: <strong>{fmtDateTime(order.sample.collected_at)}</strong></span>
              {order.sample.quantity && <span>Qty: <strong>{order.sample.quantity}</strong></span>}
            </div>
          )}

          {/* Test items */}
          {(order.items || []).map((item, ii) => {
            const params   = item.test?.parameters || item.parameters || [];
            const testName = item.test_name || item.test?.name || 'Unknown Test';
            const testCode = item.test_code || item.test?.code || '';
            const catName  = item.test?.category_name || item.test?.category?.name || '';

            return (
              <div key={ii} style={{ borderBottom: ii < (order.items?.length - 1) ? `1px solid ${C.slate200}` : 'none' }}>
                {/* Test sub-header */}
                <div style={{ background: '#e8edf2', padding: '8px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span style={{ fontWeight: 800, fontSize: 13, color: C.navy, textTransform: 'uppercase', letterSpacing: '.04em' }}>{testName}</span>
                    {testCode && <span style={{ fontSize: 11, color: C.textMuted, marginLeft: 10, fontFamily: C.mono }}>{testCode}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {catName && <span style={{ fontSize: 11, color: C.textMuted }}>{catName}</span>}
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                      background: item.status === 'COMPLETED' ? C.greenLight : C.slate100,
                      color: item.status === 'COMPLETED' ? C.green : C.textMuted,
                    }}>{item.status_display || item.status}</span>
                  </div>
                </div>

                {/* Parameters table */}
                {params.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: C.slate100, borderBottom: `1px solid ${C.slate200}` }}>
                        {['Specimen', 'Parameter', 'Result', 'Flag', 'Units', 'Reference Range', 'Notes'].map(h => (
                          <th key={h} style={{ padding: '7px 14px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: C.slate, textTransform: 'uppercase', letterSpacing: '.06em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {params.map((param, pi) => {
                        const r = (order.results || []).find(res => res.parameter === param.id || res.parameter_id === param.id);
                        const val = r?.value_numeric != null ? String(r.value_numeric) : (r?.value_text || '');
                        const flag = r?.flag || (val ? 'NORMAL' : null);
                        const isAbn = flag === 'HIGH' || flag === 'LOW' || flag === 'CRITICAL';
                        const sym  = flag === 'HIGH' ? '↑' : flag === 'LOW' ? '↓' : flag === 'CRITICAL' ? '‼' : '';
                        const ref  = param.normal_range_text
                          || (param.normal_min != null && param.normal_max != null ? `${param.normal_min} – ${param.normal_max}` : '—');
                        const rowBg = flag === 'CRITICAL' ? C.redLight : isAbn ? C.amberLight : pi % 2 === 0 ? '#fff' : '#fafbfc';

                        return (
                          <tr key={pi} className="rg-result-row" style={{ borderBottom: `1px solid ${C.slate100}`, background: rowBg }}>
                            <td style={{ padding: '9px 14px', color: C.textMuted, fontSize: 12 }}>{param.specimen || 'Serum'}</td>
                            <td style={{ padding: '9px 14px', fontSize: 13, color: C.textPrimary, fontWeight: 600 }}>
                              {param.name}
                              {param.unit && <span style={{ color: C.textMuted, fontSize: 11, marginLeft: 5 }}>({param.unit})</span>}
                            </td>
                            <td style={{ padding: '9px 14px', fontWeight: isAbn ? 800 : 500, fontSize: 14, color: flag === 'CRITICAL' ? C.red : isAbn ? C.amber : C.navy }}>
                              {val || <span style={{ color: C.textMuted, fontStyle: 'italic', fontSize: 12, fontWeight: 400 }}>Pending</span>}
                            </td>
                            <td style={{ padding: '9px 14px', fontWeight: 800, fontSize: 16, color: flag === 'CRITICAL' ? C.red : flag === 'HIGH' ? C.amber : flag === 'LOW' ? C.blue : 'transparent' }}>
                              {sym}
                            </td>
                            <td style={{ padding: '9px 14px', color: C.textMuted, fontSize: 12 }}>{param.unit || '—'}</td>
                            <td style={{ padding: '9px 14px', color: C.textSecondary, fontSize: 12, whiteSpace: 'pre-line' }}>{ref}</td>
                            <td style={{ padding: '9px 14px', color: C.textMuted, fontSize: 12 }}>{r?.notes || ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: '14px 18px', color: C.textMuted, fontStyle: 'italic', fontSize: 13 }}>No parameters defined for this test</div>
                )}
              </div>
            );
          })}

          {/* Comments */}
          {order.special_instructions && (
            <div style={{ padding: '10px 18px', background: C.amberLight, borderTop: `1px solid ${C.amberMid}`, fontSize: 13, color: C.amber }}>
              <strong>Comments / Instructions:</strong> {order.special_instructions}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ─── LabReportModal ───────────────────────────────────────────────────────────
const LabReportModal = ({ record, labOrders, loading, onClose }) => {
  const p       = record.patient || {};
  const patName = `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—';

  const handleDownload = () => {
    downloadHTML(generateLabReportHTML(record, labOrders), `Lab_Report_${patName.replace(/\s+/g, '_')}_${record.token_number || record.id}.html`);
  };
  const handlePrint = () => {
    printInNewTab(generateLabReportHTML(record, labOrders), `Lab Report — ${patName}`);
  };

  return (
    <div className="rg-modal-overlay" onClick={onClose}>
      <div className="rg-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 1040 }}>
        {/* Header */}
        <div style={{ padding: '20px 28px', background: 'linear-gradient(135deg,#0d4f6b,#0a3a50)', borderRadius: '18px 18px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,.5)', marginBottom: 6 }}>Laboratory Investigation Report</div>
            <h2 className="rg-serif" style={{ margin: 0, fontSize: 20, color: '#fff', fontWeight: 700 }}>{patName}</h2>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.6)', marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '6px 18px' }}>
              <span>ID: <strong style={{ color: '#fff', fontFamily: C.mono }}>{p.patient_id || '—'}</strong></span>
              <span>Token: <strong style={{ color: '#7dd3fc', fontFamily: C.mono, letterSpacing: 1 }}>{record.token_number || '—'}</strong></span>
              <span>Visit: <strong style={{ color: '#fff' }}>{fmtDate(record.visit_date || record.created_at)}</strong></span>
              <span>{labOrders.length} order(s) · {labOrders.filter(o => o.status === 'COMPLETED').length} completed</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
            <button className="rg-btn rg-btn-outline" onClick={handlePrint} disabled={loading} style={{ color: '#fff', borderColor: 'rgba(255,255,255,.3)', background: 'rgba(255,255,255,.1)' }}>🖨 Print</button>
            <button className="rg-btn rg-btn-teal" onClick={handleDownload} disabled={loading}>⬇ Download</button>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.3)', width: 34, height: 34, borderRadius: '50%', cursor: 'pointer', fontSize: 16, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 160px)', padding: '24px 28px' }}>
          {loading ? <LoadingSpinner message="Loading lab results…" /> : (
            <>
              {/* Patient strip */}
              <div className="rg-section-card" style={{ marginBottom: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 40px' }}>
                  {[
                    ['Patient ID',    p.patient_id || '—'],
                    ['Sample ID',     labOrders[0]?.sample?.barcode || labOrders[0]?.id || '—'],
                    ['Patient Name',  patName],
                    ['Collected',     fmtDateTime(labOrders[0]?.sample?.collected_at || labOrders[0]?.order_date)],
                    ['Age / Gender',  `${p.age ? p.age + ' Y' : (calcAge(p.date_of_birth) || '—')} / ${p.gender || '—'}`],
                    ['Reported',      fmtDate(new Date().toISOString())],
                    ['Ref. Dept',     record.department || record.doctor_name || '—'],
                    ['Token',         record.token_number || '—'],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: C.slate, minWidth: 100, flexShrink: 0 }}>{label}</span>
                      <span style={{ fontSize: 13, color: C.textPrimary, fontWeight: label === 'Patient Name' ? 700 : 500 }}>: {val}</span>
                    </div>
                  ))}
                </div>
              </div>

              <LabResultsGrid labOrders={labOrders} />

              {/* Signature */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.slate200}` }}>
                <div style={{ fontSize: 11, color: C.textMuted }}>Generated: {fmtDateTime(new Date().toISOString())} · Medical ERP System</div>
                <div style={{ textAlign: 'center', minWidth: 200 }}>
                  <div style={{ borderTop: `1.5px solid ${C.navy}`, paddingTop: 8, marginTop: 40 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.navy }}>{record.doctor_name || 'Lab Director'}</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>Authorized Signatory</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── PatientSummaryModal ──────────────────────────────────────────────────────
const PatientSummaryModal = ({ record, labOrders, loading, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const p   = record.patient || {};
  const vit = record.vitals  || {};
  const patName = `${p.first_name || ''} ${p.last_name || ''}`.trim() || '—';

  const vitalItems = [
    { label: 'Blood Pressure', value: vit.blood_pressure, icon: '🩺' },
    { label: 'Pulse',          value: vit.pulse        ? `${vit.pulse} bpm`     : null, icon: '❤️'  },
    { label: 'Temperature',    value: vit.temperature,                                  icon: '🌡️' },
    { label: 'SpO₂',           value: vit.spo2         ? `${vit.spo2}%`         : null, icon: '💨' },
    { label: 'Weight',         value: vit.weight       ? `${vit.weight} kg`     : null, icon: '⚖️' },
    { label: 'Height',         value: vit.height       ? `${vit.height} cm`     : null, icon: '📏' },
    { label: 'BMI',            value: vit.bmi,                                          icon: '📊' },
    { label: 'Resp Rate',      value: vit.respiratory_rate ? `${vit.respiratory_rate}/min` : null, icon: '🫁' },
  ].filter(v => v.value);

  const histFields = [
    ['Chief Complaint',            record.chief_complaint],
    ['History of Present Illness', record.history_of_present_illness],
    ['Duration of Illness',        record.duration_of_illness],
    ['Onset',                      record.onset],
    ['Aggravating Factors',        record.aggravating_factors],
    ['Relieving Factors',          record.relieving_factors],
    ['Associated Symptoms',        Array.isArray(record.associated_symptoms) ? record.associated_symptoms.join(', ') : record.associated_symptoms],
    ['Past Medical History',       record.past_medical_history],
    ['Surgical History',           record.surgical_history],
    ['Family History',             record.family_history],
    ['Drug History',               record.drug_history],
    ['Social History',             record.social_history],
  ].filter(([, v]) => v);

  const examFields = [
    ['General Examination',    record.general_examination],
    ['Systemic Examination',   record.systemic_examination],
    ['Cardiovascular System',  record.cardiovascular],
    ['Respiratory System',     record.respiratory],
    ['Gastrointestinal',       record.gastrointestinal],
    ['Central Nervous System', record.central_nervous_system],
    ['Musculoskeletal',        record.musculoskeletal],
    ['Local Examination',      record.local_examination],
  ].filter(([, v]) => v);

  const advFields = [
    ['Advice',                 record.advice],
    ['Diet Advice',            record.diet_advice],
    ['Activity Restrictions',  record.activity_restriction],
    ['Follow-Up Instructions', record.follow_up_instructions],
  ].filter(([, v]) => v);

  const handleDownload = () => {
    downloadHTML(generateSummaryHTML(record, labOrders), `Summary_${patName.replace(/\s+/g, '_')}_${record.token_number || record.id}.html`);
  };
  const handlePrint = () => {
    printInNewTab(generateSummaryHTML(record, labOrders), `Patient Summary — ${patName}`);
  };

  const tabs = [
    { id: 'overview',       label: '👤 Overview',     badge: null },
    { id: 'clinical',       label: '📋 Clinical',      badge: null },
    { id: 'medications',    label: '💊 Medications',   badge: record.prescriptions?.length || null },
    { id: 'investigations', label: '🧪 Lab Results',   badge: labOrders.length || null },
    { id: 'advice',         label: '📌 Advice',        badge: null },
  ];

  return (
    <div className="rg-modal-overlay" onClick={onClose}>
      <div className="rg-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 1040 }}>
        {/* Header */}
        <div style={{ padding: '20px 28px', background: `linear-gradient(135deg,${C.navy},${C.navyMid})`, borderRadius: '18px 18px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,.5)', marginBottom: 6 }}>Comprehensive Patient Summary</div>
            <h2 className="rg-serif" style={{ margin: 0, fontSize: 20, color: '#fff', fontWeight: 700 }}>{patName}</h2>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.6)', marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '6px 18px' }}>
              <span>ID: <strong style={{ color: '#fff', fontFamily: C.mono }}>{p.patient_id || '—'}</strong></span>
              <span>Token: <strong style={{ color: '#7dd3fc', fontFamily: C.mono, letterSpacing: 1 }}>{record.token_number || '—'}</strong></span>
              <span>Visit: <strong style={{ color: '#fff' }}>{fmtDate(record.visit_date || record.created_at)}</strong></span>
              {record.department && <span>Dept: <strong style={{ color: '#fff' }}>{record.department}</strong></span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
            <button className="rg-btn rg-btn-outline" onClick={handlePrint} disabled={loading} style={{ color: '#fff', borderColor: 'rgba(255,255,255,.3)', background: 'rgba(255,255,255,.1)' }}>
              🖨 Print Report
            </button>
            <button className="rg-btn rg-btn-green" onClick={handleDownload} disabled={loading}>⬇ Download</button>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.3)', width: 34, height: 34, borderRadius: '50%', cursor: 'pointer', fontSize: 16, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ background: '#fff', borderBottom: `1px solid ${C.slate200}`, padding: '8px 20px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`rg-tab ${activeTab === tab.id ? 'rg-tab-active' : 'rg-tab-inactive'}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {tab.badge ? (
                <span style={{ background: activeTab === tab.id ? 'rgba(255,255,255,.3)' : C.blue, color: activeTab === tab.id ? '#fff' : '#fff', padding: '0 7px', borderRadius: 99, fontSize: 11, fontWeight: 800, marginLeft: 2 }}>
                  {tab.badge}
                </span>
              ) : null}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 220px)', padding: '20px 24px' }}>
          {loading ? <LoadingSpinner message="Loading full patient record…" /> : (
            <>
              {/* ── OVERVIEW TAB ─────────────────────────────────────────── */}
              {activeTab === 'overview' && (
                <>
                  <div className="rg-section-card">
                    <SectionHeader icon="👤" title="Patient Demographics" color={C.blue} />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px 24px', marginBottom: 16 }}>
                      <InfoPair label="Full Name"   value={patName} />
                      <InfoPair label="Patient ID"  value={String(p.patient_id || '') || undefined} mono />
                      <InfoPair label="Date of Birth" value={fmtDate(p.date_of_birth)} />
                      <InfoPair label="Age"         value={p.age ? p.age + ' yrs' : calcAge(p.date_of_birth)} />
                      <InfoPair label="Gender"      value={p.gender} />
                      <InfoPair label="Blood Group" value={p.blood_group} />
                      <InfoPair label="Phone"       value={p.phone} />
                      <InfoPair label="Email"       value={p.email} />
                      <InfoPair label="Emergency Contact" value={p.emergency_contact} />
                      <InfoPair label="Relation"    value={p.emergency_relation} />
                      <InfoPair label="Aadhar Number" value={p.aadhar} mono />
                      <InfoPair label="ABHA Number" value={p.abha} mono />
                    </div>
                    {p.address && (
                      <div style={{ marginBottom: 12 }}>
                        <div className="rg-label">Address</div>
                        <div className="rg-value">{p.address}</div>
                      </div>
                    )}
                    {p.previous_conditions && (
                      <div style={{ marginBottom: 12 }}>
                        <div className="rg-label">Known Medical Conditions</div>
                        <div className="rg-value">{p.previous_conditions}</div>
                      </div>
                    )}
                    {p.current_medications && (
                      <div style={{ marginBottom: 12 }}>
                        <div className="rg-label">Current Medications (Pre-Visit)</div>
                        <div className="rg-value">{p.current_medications}</div>
                      </div>
                    )}
                    {p.allergies && (
                      <div style={{ background: C.amberLight, border: `1px solid ${C.amberMid}`, borderRadius: 8, padding: '10px 14px' }}>
                        <div className="rg-label" style={{ color: C.amber }}>⚠️ Known Allergies</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: C.amber, marginTop: 4 }}>{p.allergies}</div>
                      </div>
                    )}
                  </div>

                  <div className="rg-section-card">
                    <SectionHeader icon="🏥" title="Visit Information" color={C.green} />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '14px 24px', marginBottom: 14 }}>
                      <InfoPair label="Visit Date"          value={fmtDate(record.visit_date || record.created_at)} />
                      <InfoPair label="Visit Time"          value={fmtTime(record.visit_date || record.created_at)} />
                      <InfoPair label="OPD Token"           value={record.token_number} mono />
                      <InfoPair label="Department"          value={record.department} />
                      <InfoPair label="Consulting Doctor"   value={record.doctor_name} />
                      <InfoPair label="Consultation Started" value={fmtDateTime(record.consultation_started_at)} />
                      <InfoPair label="Consultation Completed" value={fmtDateTime(record.consultation_completed_at)} />
                      <InfoPair label="Follow-Up Date"      value={fmtDate(record.follow_up_date)} />
                    </div>
                    {record.follow_up_instructions && (
                      <div>
                        <div className="rg-label">Follow-Up Instructions</div>
                        <ProseBlock value={record.follow_up_instructions} color={C.green} />
                      </div>
                    )}
                  </div>

                  <div className="rg-section-card">
                    <SectionHeader icon="❤️" title="Vital Signs" color={C.red} />
                    {vitalItems.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        {vitalItems.map(({ label, value, icon }) => (
                          <div key={label} style={{ background: C.slate100, borderRadius: 10, padding: '12px 20px', textAlign: 'center', minWidth: 110, border: `1px solid ${C.slate200}`, flex: '0 0 auto' }}>
                            <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: C.blue, lineHeight: 1.1 }}>{value}</div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 5 }}>{label}</div>
                          </div>
                        ))}
                      </div>
                    ) : <EmptyState message="No vitals recorded for this visit" />}
                  </div>
                </>
              )}

              {/* ── CLINICAL TAB ─────────────────────────────────────────── */}
              {activeTab === 'clinical' && (
                <>
                  <div className="rg-section-card">
                    <SectionHeader icon="📋" title="Chief Complaint & Clinical History" color={C.amber} />
                    {histFields.length > 0 ? (
                      <div style={{ display: 'grid', gap: 16 }}>
                        {histFields.map(([label, value]) => (
                          <div key={label}>
                            <div className="rg-label">{label}</div>
                            <ProseBlock value={value} color={C.amber} />
                          </div>
                        ))}
                      </div>
                    ) : <EmptyState message="No complaint or history recorded" />}
                  </div>

                  <div className="rg-section-card">
                    <SectionHeader icon="🔬" title="Physical Examination Findings" color={C.violet} />
                    {examFields.length > 0 ? (
                      <div style={{ display: 'grid', gap: 16 }}>
                        {examFields.map(([label, value]) => (
                          <div key={label}>
                            <div className="rg-label">{label}</div>
                            <ProseBlock value={value} color={C.violet} />
                          </div>
                        ))}
                      </div>
                    ) : <EmptyState message="No examination findings recorded" />}
                  </div>

                  <div className="rg-section-card">
                    <SectionHeader icon="🩻" title="Diagnosis" color={C.navy} count={record.diagnoses?.length} />
                    {record.diagnoses?.length > 0 ? (
                      <div>
                        {record.diagnoses.map((d, i) => (
                          <div key={i} style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: i < record.diagnoses.length - 1 ? `1px solid ${C.slate200}` : 'none' }}>
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: C.navy, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, flexShrink: 0, marginTop: 2 }}>{i + 1}</div>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: 15, color: C.textPrimary }}>{d.name || d.diagnosis_name || String(d)}</div>
                              {d.icd_code && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2, fontFamily: C.mono }}>ICD: {d.icd_code}</div>}
                              {d.notes    && <div style={{ fontSize: 13, color: C.textSecondary, marginTop: 6, lineHeight: 1.6 }}>{d.notes}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <EmptyState message="No diagnoses recorded" />}
                    {record.differential_diagnosis && (
                      <div style={{ marginTop: 16 }}>
                        <div className="rg-label">Differential Diagnosis</div>
                        <ProseBlock value={record.differential_diagnosis} color={C.blue} />
                      </div>
                    )}
                  </div>

                  {record.doctor_notes && (
                    <div className="rg-section-card">
                      <SectionHeader icon="📝" title="Doctor Notes & Clinical Remarks" color={C.navyMid} />
                      <ProseBlock value={record.doctor_notes} color={C.navyMid} />
                    </div>
                  )}
                </>
              )}

              {/* ── MEDICATIONS TAB ──────────────────────────────────────── */}
              {activeTab === 'medications' && (
                <>
                  <div className="rg-section-card">
                    <SectionHeader icon="💊" title="Prescriptions & Medications" color={C.green} count={record.prescriptions?.length} />
                    {record.prescriptions?.length > 0 ? (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr>
                              {['#', 'Medication', 'Dose / Form', 'Route', 'Frequency', 'Duration', 'Instructions'].map(h => (
                                <th key={h} style={{ padding: '9px 14px', textAlign: 'left', background: C.slate100, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: C.slate, borderBottom: `2px solid ${C.slate200}`, whiteSpace: 'nowrap' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {record.prescriptions.map((m, i) => (
                              <tr key={i} style={{ borderBottom: `1px solid ${C.slate100}` }}>
                                <td style={{ padding: '11px 14px', fontWeight: 700, color: C.textMuted }}>{i + 1}</td>
                                <td style={{ padding: '11px 14px', fontWeight: 700, color: C.textPrimary }}>{m.drug_name || m.name || '—'}</td>
                                <td style={{ padding: '11px 14px', color: C.textSecondary }}>{[m.dose || m.dosage, m.form].filter(Boolean).join(' ') || '—'}</td>
                                <td style={{ padding: '11px 14px', color: C.textSecondary }}>{m.route || '—'}</td>
                                <td style={{ padding: '11px 14px', color: C.textSecondary }}>{m.frequency || '—'}</td>
                                <td style={{ padding: '11px 14px', color: C.textSecondary, whiteSpace: 'nowrap' }}>{[m.duration, m.duration_unit].filter(Boolean).join(' ') || '—'}</td>
                                <td style={{ padding: '11px 14px', color: C.textSecondary, fontSize: 12 }}>{m.instructions || m.special_instructions || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : <EmptyState message="No medications prescribed" />}
                    {record.treatment_plan && (
                      <div style={{ marginTop: 18 }}>
                        <div className="rg-label">Treatment Plan</div>
                        <ProseBlock value={record.treatment_plan} color={C.green} />
                      </div>
                    )}
                  </div>

                  {/* Investigations ordered */}
                  <div className="rg-section-card">
                    <SectionHeader icon="🔍" title="Investigations Ordered" color={C.teal} count={(record.investigations_detail || []).length || undefined} />
                    {(record.investigations_detail || []).length > 0 ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr>{['#', 'Investigation', 'Instructions / Notes'].map(h => (
                            <th key={h} style={{ padding: '8px 14px', textAlign: 'left', background: C.slate100, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.06em', color: C.slate, borderBottom: `2px solid ${C.slate200}` }}>{h}</th>
                          ))}</tr>
                        </thead>
                        <tbody>
                          {record.investigations_detail.map((inv, i) => (
                            <tr key={i} style={{ borderBottom: `1px solid ${C.slate100}` }}>
                              <td style={{ padding: '10px 14px', fontWeight: 700, color: C.textMuted }}>{i + 1}</td>
                              <td style={{ padding: '10px 14px', fontWeight: 700, color: C.textPrimary }}>{inv.name || '—'}</td>
                              <td style={{ padding: '10px 14px', color: C.textSecondary, fontSize: 12 }}>{inv.notes || inv.instructions || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : record.investigations_text ? (
                      <ProseBlock value={record.investigations_text} color={C.teal} />
                    ) : <EmptyState message="No investigations ordered" />}
                  </div>
                </>
              )}

              {/* ── LAB RESULTS TAB ──────────────────────────────────────── */}
              {activeTab === 'investigations' && (
                <div className="rg-section-card">
                  <SectionHeader icon="🧪" title="Laboratory Results" color="#0369a1" count={labOrders.length} />
                  <LabResultsGrid labOrders={labOrders} />
                </div>
              )}

              {/* ── ADVICE TAB ───────────────────────────────────────────── */}
              {activeTab === 'advice' && (
                <>
                  {advFields.length > 0 ? (
                    <div className="rg-section-card">
                      <SectionHeader icon="📌" title="Advice & Follow-Up Instructions" color={C.blue} />
                      <div style={{ display: 'grid', gap: 16 }}>
                        {advFields.map(([label, value]) => (
                          <div key={label}>
                            <div className="rg-label">{label}</div>
                            <ProseBlock value={value} color={C.blue} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : <div className="rg-section-card"><EmptyState message="No advice or follow-up instructions recorded" /></div>}

                  {record.follow_up_date && (
                    <div className="rg-section-card" style={{ background: C.greenLight, border: `1px solid ${C.greenMid}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ fontSize: 28 }}>📅</div>
                        <div>
                          <div className="rg-label" style={{ color: C.green }}>Next Follow-Up Appointment</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: C.green }}>{fmtDate(record.follow_up_date)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Referral info if any */}
                  {record.referral && typeof record.referral === 'object' && Object.keys(record.referral).length > 0 && (
                    <div className="rg-section-card">
                      <SectionHeader icon="↪️" title="Referral / Transfer Information" color={C.violet} />
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '12px 24px' }}>
                        {record.referral.transferred_to_doctor_name && (
                          <InfoPair label="Referred To" value={record.referral.transferred_to_doctor_name} />
                        )}
                        {record.referral.transfer_reason && (
                          <InfoPair label="Reason" value={record.referral.transfer_reason} />
                        )}
                        {record.referral.transferred_at && (
                          <InfoPair label="Transferred At" value={fmtDateTime(record.referral.transferred_at)} />
                        )}
                        {record.referral.transferred_by_name && (
                          <InfoPair label="Transferred By" value={record.referral.transferred_by_name} />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Contributors */}
                  {record.contributors && Object.keys(record.contributors).length > 0 && (
                    <div className="rg-section-card">
                      <SectionHeader icon="👥" title="Section Contributors" color={C.slate} />
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '10px 24px' }}>
                        {Object.entries(record.contributors).map(([section, info]) => (
                          <div key={section} style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '6px 0', borderBottom: `1px solid ${C.slate100}` }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: C.slate, textTransform: 'capitalize', minWidth: 120 }}>{section.replace(/_/g, ' ')}</span>
                            <span style={{ fontSize: 13, color: C.textPrimary }}>{info.doctor_name || '—'}</span>
                            <span style={{ fontSize: 11, color: C.textMuted, marginLeft: 'auto' }}>{fmtDate(info.at)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Signature strip (visible in all tabs) */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.slate200}` }}>
                <div style={{ textAlign: 'center', minWidth: 200 }}>
                  <div style={{ borderTop: `1.5px solid ${C.navy}`, paddingTop: 8, marginTop: 40 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.navy }}>{record.doctor_name || 'Treating Physician'}</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>Signature & Stamp</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const ReportGeneration = () => {
  const { user } = useAuth();
  const [records,       setRecords]       = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [fetchError,    setFetchError]    = useState('');
  const [searchQ,       setSearchQ]       = useState('');
  const [sortBy,        setSortBy]        = useState('date_desc');
  const [dateFrom,      setDateFrom]      = useState('');
  const [dateTo,        setDateTo]        = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [labOrders,     setLabOrders]     = useState([]);
  const [modalLoading,  setModalLoading]  = useState(false);
  const [modalMode,     setModalMode]     = useState(null); // 'lab' | 'summary'

  useEffect(() => { injectStyles(); }, []);

  const fetchRecords = useCallback(async () => {
    setLoading(true); setFetchError('');
    try {
      const params = { status: 'COMPLETED' };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      const data = await receptionAPI.getTokens(params);
      setRecords(Array.isArray(data) ? data : (data.results || []));
    } catch (e) {
      if (e.response?.status === 403)
        setFetchError('403 Forbidden — REPORT_GENERATION role needs read access to GET /auth/reception/tokens/ in views.py');
      else
        setFetchError(`Failed to load records: ${e.message}`);
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const openModal = useCallback(async (token, mode) => {
    setModalMode(mode);
    setSelectedRecord(normalizeRecord({}, token));
    setLabOrders([]); setModalLoading(true);
    try {
      const opdId = token.opd_record_id;

      // Fetch full OPD record for complete clinical data
      let fullRecord = token.record_data;
      if ((!fullRecord || Object.keys(fullRecord).length < 5) && opdId) {
        fullRecord = await receptionAPI.getOPDRecord(opdId).catch(() => token.record_data || {});
      }
      setSelectedRecord(normalizeRecord(fullRecord || {}, token));

      // Fetch lab orders with full detail (including results & parameters)
      if (opdId) {
        const ordersRaw  = await labAPI.getOrders({ opd_record_id: opdId }).catch(() => []);
        const ordersList = Array.isArray(ordersRaw) ? ordersRaw : (ordersRaw?.results || []);
        // Fetch each order with full detail (results, parameters)
        const detailed = await Promise.all(
          ordersList.map(o => labAPI.getOrder(o.id).catch(() => o))
        );
        setLabOrders(detailed);
      }
    } catch (e) {
      console.error('Failed to load report data:', e);
    } finally { setModalLoading(false); }
  }, []);

  const closeModal = () => { setSelectedRecord(null); setLabOrders([]); setModalMode(null); };

  // Filtering & sorting
  const filtered = records.filter(r => {
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    return (
      (r.patient_name   || '').toLowerCase().includes(q) ||
      (r.patient_id_num || '').toLowerCase().includes(q) ||
      (r.token_number   || '').toLowerCase().includes(q) ||
      (r.doctor_name    || '').toLowerCase().includes(q) ||
      (r.department_name|| '').toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'date_desc') return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    if (sortBy === 'date_asc')  return new Date(a.created_at || 0) - new Date(b.created_at || 0);
    if (sortBy === 'name_asc')  return (a.patient_name || '').localeCompare(b.patient_name || '');
    if (sortBy === 'name_desc') return (b.patient_name || '').localeCompare(a.patient_name || '');
    return 0;
  });

  // Stats
  const total     = records.length;
  const today     = records.filter(r => { try { return new Date(r.created_at).toDateString() === new Date().toDateString(); } catch { return false; } }).length;
  const withLab   = records.filter(r => r.opd_record_id).length;

  const thS = { padding: '11px 16px', textAlign: 'left', background: C.slate100, color: C.slate, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: `2px solid ${C.slate200}`, whiteSpace: 'nowrap' };
  const tdS = { padding: '14px 16px', borderBottom: `1px solid ${C.slate100}`, color: C.textPrimary, verticalAlign: 'middle' };

  return (
    <DashboardLayout title="Report Generation">
      <div className="rg-root" style={{ minHeight: '100vh', background: C.pageBg, padding: '24px 28px' }}>

        {/* Page title */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 46, height: 46, borderRadius: 13, background: `linear-gradient(135deg,${C.blue},#14547e)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, boxShadow: '0 4px 16px rgba(29,108,168,.35)' }}>📋</div>
            <div>
              <h1 className="rg-serif" style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.navy }}>Report Generation</h1>
              <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>Completed consultations · Lab reports & Patient summaries</div>
            </div>
          </div>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 20 }}>
          {[
            { label: 'Total Completed', value: total,   icon: '✅', color: C.green  },
            { label: 'Today',           value: today,   icon: '📅', color: C.blue   },
            { label: 'With Lab Orders', value: withLab, icon: '🧪', color: C.violet },
          ].map(({ label, value, icon, color }) => (
            <div key={label} style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.slate200}`, padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 2px 8px rgba(0,0,0,.04)' }}>
              <div style={{ width: 46, height: 46, borderRadius: 11, background: color + '1a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{icon}</div>
              <div>
                <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1, marginBottom: 4 }}>{value}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.textSecondary }}>{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${C.slate200}`, padding: '14px 18px', marginBottom: 18, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}>
          <input
            className="rg-input"
            style={{ width: 300 }}
            placeholder="Search patient name, ID, token, doctor, department…"
            value={searchQ}
            onChange={e => setSearchQ(e.target.value)}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, whiteSpace: 'nowrap' }}>From</label>
            <input className="rg-input" style={{ width: 150 }} type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, whiteSpace: 'nowrap' }}>To</label>
            <input className="rg-input" style={{ width: 150 }} type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </div>
          <select
            style={{ padding: '9px 12px', border: `1.5px solid ${C.slate200}`, borderRadius: 8, fontSize: 13, color: C.textPrimary, background: '#fff', outline: 'none', cursor: 'pointer' }}
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
          >
            <option value="date_desc">Newest First</option>
            <option value="date_asc">Oldest First</option>
            <option value="name_asc">Patient A → Z</option>
            <option value="name_desc">Patient Z → A</option>
          </select>
          {(searchQ || dateFrom || dateTo) && (
            <button className="rg-btn rg-btn-outline" onClick={() => { setSearchQ(''); setDateFrom(''); setDateTo(''); }}>✕ Clear</button>
          )}
        </div>

        {/* Error */}
        {fetchError && (
          <div style={{ background: C.redLight, border: `1px solid ${C.redMid}`, borderRadius: 10, padding: '13px 18px', marginBottom: 16, fontSize: 13, color: C.red, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>⛔</span>
            <div><strong>Error</strong><div style={{ marginTop: 4, lineHeight: 1.65 }}>{fetchError}</div></div>
          </div>
        )}

        {/* Table */}
        <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${C.slate200}`, boxShadow: '0 2px 14px rgba(0,0,0,.06)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.slate200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: C.slate100 }}>
            <span className="rg-serif" style={{ fontSize: 14, fontWeight: 700, color: C.navy }}>
              Completed Consultations
              <span style={{ fontSize: 12, color: C.textMuted, marginLeft: 10, fontWeight: 400 }}>{sorted.length} record{sorted.length !== 1 ? 's' : ''}</span>
            </span>
            <div style={{ fontSize: 12, color: C.textMuted }}>
              Click <strong>🧪 Lab Report</strong> or <strong>📋 Summary</strong> to open
            </div>
          </div>

          {loading ? (
            <LoadingSpinner message="Loading completed consultations…" />
          ) : sorted.length === 0 ? (
            <div style={{ padding: '64px 0', textAlign: 'center', color: C.textMuted }}>
              <div style={{ fontSize: 42, marginBottom: 14 }}>📭</div>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 6 }}>No records found</div>
              <div style={{ fontSize: 13 }}>{searchQ ? 'Try adjusting your search.' : 'Consultations marked COMPLETED will appear here.'}</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Patient', 'Patient ID', 'Token', 'Visit Date', 'Doctor', 'Department', '🧪 Lab Report', '📋 Patient Summary'].map(h => (
                      <th key={h} style={thS}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(token => (
                    <tr key={token.id} className="rg-tr">
                      <td style={tdS}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: C.navy }}>{token.patient_name || '—'}</div>
                      </td>
                      <td style={tdS}>
                        <span style={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.blue }}>
                          {token.patient_id_num || '—'}
                        </span>
                      </td>
                      <td style={tdS}>
                        <span style={{ fontFamily: C.mono, fontWeight: 700, color: C.navyMid }}>
                          {token.token_number || '—'}
                        </span>
                      </td>
                      <td style={tdS}>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{fmtDate(token.created_at)}</div>
                        <div style={{ fontSize: 11, color: C.textMuted }}>{fmtTime(token.created_at)}</div>
                      </td>
                      <td style={tdS}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{token.doctor_name || '—'}</div>
                      </td>
                      <td style={tdS}>
                        <span style={{ background: C.blueLight, color: C.blue, padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
                          {token.department_name || '—'}
                        </span>
                      </td>
                      <td style={tdS} onClick={e => e.stopPropagation()}>
                        <button
                          className="rg-btn rg-btn-teal"
                          style={{ padding: '7px 14px', fontSize: 12 }}
                          onClick={() => openModal(token, 'lab')}
                        >
                          🧪 Lab Report
                        </button>
                      </td>
                      <td style={tdS} onClick={e => e.stopPropagation()}>
                        <button
                          className="rg-btn rg-btn-primary"
                          style={{ padding: '7px 14px', fontSize: 12 }}
                          onClick={() => openModal(token, 'summary')}
                        >
                          📋 Full Summary
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {selectedRecord && modalMode === 'lab' && (
        <LabReportModal
          record={selectedRecord}
          labOrders={labOrders}
          loading={modalLoading}
          onClose={closeModal}
        />
      )}
      {selectedRecord && modalMode === 'summary' && (
        <PatientSummaryModal
          record={selectedRecord}
          labOrders={labOrders}
          loading={modalLoading}
          onClose={closeModal}
        />
      )}
    </DashboardLayout>
  );
};

export default ReportGeneration;